export const texts = {
  activities: {
    date: 'Date',
    empty: 'No activity yet',
    emptyText: 'Activities are logged when you or your team members make changes.',
    event: 'Event',
    headline: 'Activity',
    system: 'System',
    user: 'User',
    viewDeployment: 'View Deployment',
  },
  common: {
    added: 'Added',
    administration: 'Administration',
    administrator: 'Administrator',
    apiKey: 'API Key',
    appName: 'Pangostack',
    builtIn: 'Built In',
    cancel: 'Cancel',
    common: 'Common',
    confirm: 'Confirm',
    copied: 'Text added to clipboard',
    createdAt: 'Created',
    currency: 'Currency',
    dangerZone: 'Danger Zone',
    dashboard: 'Dashboard',
    default: 'Default',
    description: 'Description',
    disabled: 'Disabled',
    documentation: 'Official Documentation',
    documentationLabel: 'Read more at the',
    dropZone: 'Drag and drop some file here, or click to select files.',
    dropZoneDrop: 'Drop files.',
    edit: 'Edit',
    email: 'Email',
    enabled: 'Enabled',
    endpoint: 'Endpoint',
    environment: 'Environment',
    error: 'Failed to make update.',
    failed: 'Failed',
    generate: 'Generate',
    headers: 'Headers',
    health: 'Health',
    healthy: 'Healthy',
    hideFileUpload: 'Hide Files Sidebar',
    hour: 'hour',
    immutable: 'Immutable',
    installation: 'Installation',
    installing: 'Installing',
    instructions: 'Instructions',
    loginButton: 'Login',
    loginFailed: 'Username or password not correct.',
    loginText: 'Login with username and password',
    logout: 'Logout',
    logViewer: 'Log Viewer',
    management: 'Management',
    metrics: 'Metrics',
    month: 'month',
    more: 'More',
    name: 'Name',
    no: 'No',
    notFound: 'Not found',
    notPublic: 'Not public',
    notReady: 'Not ready',
    overview: 'Overview',
    page: (page: number, total: number) => `Page ${page} of ${total}`,
    password: 'Password',
    passwordConfirm: 'Confirm Password',
    passwordsDoNotMatch: 'Passwords do not match.',
    pending: 'Pending',
    perDay: 'day',
    perHour: 'per hour',
    perMonth: 'per month',
    priceFrom: 'from',
    priceFromHint: 'Depending on the selected resources.',
    remove: 'Remove',
    rename: 'Rename',
    required: 'Required field',
    roles: 'Roles',
    save: 'Save',
    saved: 'Saved successfully.',
    search: 'Search',
    service: 'Service',
    settings: 'Settings',
    soon: 'Soon',
    status: 'Status',
    subtitle: 'Subtitle',
    succeeded: 'Succeeded',
    text: 'Text',
    title: 'Title',
    totalPrice: 'Total',
    unhealthy: 'Unhealthy',
    uncheckedHealth: 'Health unchecked',
    url: 'Url',
    usage: 'Usage',
    userGroup: 'User Group',
    userGroups: 'User Groups',
    version: 'Version',
    yes: 'Yes',
  },
  billing: {
    amount: 'Amount',
    dueDate: 'Due Date',
    emptyLabel: 'No invoice yet',
    emptyText: 'Wait for the first billing period or create a new deployment.',
    headline: 'Billing',
    notPaid: 'Not Paid',
    number: 'Invoice',
    paid: 'Paid',
    paymentDue: 'Payment Due',
    pending: 'Pending',
    portal: 'Payment Portal',
    voided: 'Voided',
  },
  dashboard: {},
  deployments: {
    basePrice: 'Base Price',
    checkChartWarning: 'Days are based on UTC time. Health checks are tested every 15 minutes.',
    create: 'New Deployment',
    deleteConfirmText: 'Do you really want to delete the Deployment?',
    deleteConfirmTitle: 'Delete Member',
    deleteConfirmed: 'Deletion confirmed. It might take a while until all resources have been deleted.',
    deployButton: 'Deploy Now',
    deployHeadline: 'Deploy new Service',
    deployingInfo: 'This instance is currently deployed. Your resources may still be finishing installing and booting up.',
    deploymentHeadline: 'Deployment',
    edit: 'Edit Deployment',
    empty: 'No deployments found',
    emptyLabel: 'No deployment yet',
    emptyText: 'Install a service to create your first deployment.',
    emptyLog: 'No log found',
    emptyLogText: 'This deployment does not provide any logs.',
    estimatedPrice: 'Estimated Price',
    estimatedPriceText: 'Price is monthly, based on a 30-day average, assuming no changes to parameters.',
    fixedPrice: 'Fixed Price',
    headline: 'Deployments',
    immutableTooltip: 'This field cannot be changed anymore after the deployment has been created.',
    loadingDetails: 'Loading details…',
    metricsChartWarning: 'Times are based on UTC time. Each metric is collected based on its own interval.',
    metricsEmpty: 'No metrics collected yet.',
    resourceCores: 'Cores',
    resourceMemory: 'Memory',
    resources: 'Resources',
    resourceStorage: 'Storage',
    resourceVolumes: 'Volumes',
    retryButton: 'Retry',
    retryConfirmed: 'Retry started. It might take a while until all resources have been deployed.',
    retryConfirmText: 'Do you really want to retry this failed deployment? The deployment steps will run again.',
    retryConfirmTitle: 'Retry Deployment',
    selectService: 'Select the Service you want to deploy',
    stepAttempt: 'Attempt',
    steps: 'Deployment Steps',
    stepsDeleteAction: 'Remove',
    stepsDeployAction: 'Deploy',
    stepsProgress: (done: number, total: number) => `${done} of ${total} steps completed`,
    totalCors: 'Cores',
    totalFailures: 'Failures',
    totalMemoryGB: 'Memory (GB)',
    totalStorageGB: 'Storage (GB)',
    totalSuccesses: 'Successes',
    totalVolumeGB: 'Volume (GB)',
    updateAvailable: 'An Update is available',
    updateHeadline: 'Update Deployment',
    usageChartWarning: 'Days are based on UTC time. Usage will be measured once per hour.',
  },
  login: {
    loginButton: (provider: string) => `Login with ${provider}`,
    loginHint: 'Login to your account',
    welcome: 'Welcome to Pangostack 👋',
  },
  members: {
    addMemberButton: 'Add Member',
    addMemberFailed: 'User not found',
    addMemberHint: 'Add a team member by email. Only existing users can be added.',
    addMemberTitle: 'Add Team Member',
    headline: 'Members',
    memberHints:
      'Team members do not have direct access to the deployed services, but they can update deployments or change billing information.',
    removeConfirmText: 'Do you really want to remove the Member?',
    removeConfirmTitle: 'Remove Member',
    role: 'Role',
  },
  services: {
    create: 'New Service',
    createFailed: 'Failed to create Service',
    createVersion: 'New Version',
    createVersionFailed: 'Failed to create Service Version',
    definition: 'YAML Definition',
    definitionCreationHint: 'The YAML Definition should not be changed later, if you have an deployment.',
    definitionUpdateHint: 'The YAML Definition should not be changed later, if you have an deployment.',
    empty: 'No service found.',
    emptyVersions: 'No version found.',
    fixedPrice: 'Fixed base price',
    headline: 'Services',
    isActive: 'Is Active',
    isPublic: 'Is Public',
    latestVersion: 'Latest Version',
    numDeployments: 'Num deployments',
    pricing: 'Pricing',
    pricePerCoreHour: 'Price per CPU / hour',
    pricePerCoreHourLabel: 'CPU / hour',
    pricePerMemoryGBHour: 'Price per GB Memory / hour',
    pricePerMemoryGBHourLabel: 'GB Memory / hour',
    pricePerStorageGBMonth: 'Price per GB Storage / month',
    pricePerStorageGBMonthLabel: 'GB Memory / hour',
    pricePerVolumeGBHour: 'Price per GB Volume / hour',
    pricePerVolumeGBHourLabel: 'GB Volume / hour',
    resourceTypes: 'Available Resources',
    resourceTypesAllowedValues: 'Allowed values',
    resourceTypesContext: 'Context',
    resourceTypesEmpty: 'No resources available.',
    resourceTypesFailed: 'Failed to load the available resources.',
    resourceTypesHint: 'These resources can be used in the YAML definition.',
    resourceTypesMetrics: 'Metrics',
    resourceTypesParameters: 'Parameters',
    resourceTypesRequired: 'Required',
    update: 'Update Service',
    updateFailed: 'Failed to update Service',
    updateVersion: 'Update Service Version',
    updateVersionFailed: 'Failed to update Service Version',
    updateVersionSucceeded: 'Service Version Updated',
    versions: 'Versions',
    verify: 'Verify',
    verifySuccess: 'Service definition is valid.',
    verifyFailed: 'Service definition not valid.',
  },
  userGroups: {
    create: 'Create User Group',
    createFailed: 'Failed to create User Group',
    empty: 'No user groups found.',
    removeConfirmText: 'Do you really want to remove the User Group?',
    removeConfirmTitle: 'Remove User Group',
    headline: 'Groups',
    update: 'Update User Group',
    updateFailed: 'Failed to update User Group',
  },
  users: {
    create: 'Create User',
    createFailed: 'Failed to ceate User',
    empty: 'No users found.',
    removeConfirmText: 'Do you really want to remove the User?',
    removeConfirmTitle: 'Remove User',
    headline: 'Users',
    update: 'Update User',
    updateFailed: 'Failed to update User',
  },
  teams: {
    createTitle: 'Create Team',
    createText: 'You are not part of any team yet. Create a new one to invite other members and manage resources.',
    selectTeam: 'Select Team',
  },
  theme: {
    appName: 'App Name',
    appNameHints: 'The name of this app, used for the title and the menu.',
    customCss: 'Custom CSS',
    favicon: 'Favicon',
    faviconHint: 'The icon shown in the browser tab',
    files: 'Files',
    footerLinks: 'Footer Links',
    footerText: 'Footer Text',
    headerColor: 'Header Color',
    headline: 'Theme',
    headerLinks: 'Header Links',
    loginLogo: 'Login Logo',
    loginLogoHints: 'The logo for the login page.',
    loginBackground: 'Login Image',
    loginBackgroundHints: 'The background image for the login page.',
    logo: 'Logo',
    logoCurrent: 'Current logo',
    logoHints: 'The logo is shown on the admin page and the login screen.',
    fileUploadFailed: 'Failed to upload file.',
    fileUploadSucceeded: 'File Upload succeeded.',
    primaryColor: 'Primary Color',
    primaryContentColor: 'Primary Content Color',
    settings: 'General Settings',
    updateFailed: 'Failed to update settings.',
    welcomeText: 'Welcome Text',
    welcomeTextHints: 'Only shown on the login page.',
  },
  workers: {
    apiKey: 'API Key',
    apiKeyConfigured: 'An API key is configured. Leave empty to keep it.',
    apiKeyHints: '',
    create: 'Create Worker',
    createFailed: 'Failed to create Worker',
    empty: 'No workers found.',
    endpoint: 'Endpoint',
    endpointHints: '',
    endpointInvalid: 'Must be a URL starting with http:// or https://, e.g. http://localhost:3100.',
    headline: 'Workers',
    manage: 'Manage Workers',
    missingWarning:
      'At least one worker is required. Without a worker no resources can be provisioned and deployments will fail.',
    removeConfirmText: 'Do you really want to remove the Worker?',
    removeConfirmTitle: 'Remove Worker',
    resourceTypes: 'Resource Types',
    startedAt: 'Started At',
    unreachable: 'Unreachable',
    update: 'Update Worker',
    updateFailed: 'Failed to update Worker',
  },
};
